import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Skeleton,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ShoppingBagOutlined,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import dayjs from "dayjs";

import { OrderHistoryModel } from "../repositories/history.repository";
import { useAuthStore } from "../store/useAuthStore";
import { openLogin } from "../api/auth";
import { useOrderHistoriesView } from "../hooks/useOrderHistoriesView";
import PurchaseFilters from "../components/PurchaseFilters";

export type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
  key: string;
};

/** ---------- Status Pill (soft) ---------- */
type PillTone = "success" | "warning" | "error" | "default";
const getPillColors = (tone: PillTone, theme: any) => {
  const map = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    default: theme.palette.text.secondary,
  } as const;
  const main = map[tone] ?? theme.palette.text.secondary;
  return {
    bg: alpha(main, tone === "default" ? 0.08 : 0.12),
    bd: alpha(main, tone === "default" ? 0.16 : 0.24),
    fg: main,
  };
};

const StatusPill = ({ tone, label }: { tone: PillTone; label: string }) => {
  const theme = useTheme();
  const c = getPillColors(tone, theme);
  return (
    <Box
      component="span"
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: c.bg,
        border: `1px solid ${c.bd}`,
        color: c.fg,
        fontSize: 12,
        fontWeight: 700,
        textTransform: "capitalize",
        lineHeight: 1,
      }}
    >
      {label}
    </Box>
  );
};

const mapStatus = (status: string): { tone: PillTone; label: string } => {
  const s = (status || "").toLowerCase();
  if (["completed", "success", "paid"].includes(s))
    return { tone: "success", label: "Paid" };
  if (s === "pending") return { tone: "warning", label: "Pending" };
  if (["failed", "cancelled", "canceled"].includes(s))
    return { tone: "error", label: "Failed" };
  return { tone: "default", label: status || "Unknown" };
};

const formatAmount = (amount: string | number, currency: string = "LAK") =>
  new Intl.NumberFormat("lo-LA", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const PurchaseHistoryCard = ({ order }: { order: OrderHistoryModel }) => {
  const navigate = useNavigate();
  const { tone, label } = mapStatus(order.paymentStatus);

  const purchaseDate = new Date(order.orderDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",

        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: "background.paper",
        border: (t) => `1px solid ${t.palette.divider}`,
        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
        transition: "box-shadow .2s ease, transform .2s ease",
        "&:hover": {
          boxShadow: "0 16px 36px rgba(0,0,0,0.12)",
          transform: { sm: "translateY(-2px)" },
        },
      }}
    >
      <Box
        sx={{
          height: 3,
          bgcolor: (t) => t.palette.primary.main,
          opacity: 0.85,
        }}
      />
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={0.75}
        >
          <Stack spacing={0.25}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, lineHeight: 1.2 }}
            >
              Order #{order.orderId}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ letterSpacing: 0.2 }}
            >
              Ref: {order.paymentRef}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "inline", sm: "none" } }}
            >
              {purchaseDate}
            </Typography>
            <StatusPill tone={tone} label={label} />
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid
          container
          spacing={2}
          columns={{ xs: 12, sm: 3 }}
          justifyContent="center"
          sx={{
            mx: "auto",
            maxWidth: { xs: "100%", sm: "80%" },
            "& .detail": {
              p: { xs: 1, sm: 0 },
              borderRadius: 1.5,
              bgcolor: { xs: "action.hover", sm: "transparent" },
            },
          }}
        >
          <Grid item xs={12} sm={4} className="detail">
            <Typography variant="caption" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
              {formatAmount(order.amount)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4} className="detail">
            <Typography variant="caption" color="text.secondary">
              Payment Method
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, textTransform: "capitalize" }}
            >
              {order.paymentChannel?.replace(/_/g, " ") || "-"}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            className="detail"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            <Typography variant="caption" color="text.secondary">
              Purchase Date
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {purchaseDate}
            </Typography>
          </Grid>
        </Grid>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Button
            fullWidth
            variant="contained"
            startIcon={<VisibilityIcon />}
            onClick={() =>
              navigate(`/order/${order.orderUuid}/${order.paymentChannelId}`)
            }
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
              py: { xs: 1, sm: 1.1 },
              minHeight: 42,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            View details
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

/** ---------- Skeleton ---------- */
const PurchaseHistoryCardSkeleton = () => (
  <Card sx={{ borderRadius: 3 }}>
    <CardContent sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between">
        <Skeleton variant="text" width={140} height={24} />
        <Skeleton
          variant="rounded"
          width={76}
          height={24}
          sx={{ borderRadius: 999 }}
        />
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Skeleton variant="text" width={120} />
          <Skeleton variant="text" width={180} height={30} />
        </Grid>

        <Grid item>
          <Skeleton variant="text" width={80} />
          <Skeleton variant="text" width={120} height={30} />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1.25} mt={2}>
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{ borderRadius: 12 }}
        />
      </Stack>
    </CardContent>
  </Card>
);

/** ---------- Page ---------- */
const PurchasePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const cusId = user?.cusId ?? "";

  // use view hook (keeps your base hook intact)
  const {
    grouped,
    isLoading,
    isFetching,
    error,
    refetch,
    // filters
    status,
    setStatus,
    clearFilters,
  } = useOrderHistoriesView(cusId);

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    key: "selection",
  });
  const entries = useMemo(
    () => Array.from(grouped?.entries?.() ?? []),
    [grouped]
  );

  const handleLogin = () => openLogin();

  if (!user || !cusId) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container maxWidth="md">
          <Card
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 4,
              textAlign: "center",
              boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
              background: "white",
            }}
          >
            <ShoppingBagOutlined sx={{ fontSize: 56, color: "primary.main" }} />
            <Typography variant="h5" fontWeight={900} mt={1}>
              Sign in to see your purchases
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              Track orders, print receipts, and manage payments in one place.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleLogin}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Sign In
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/")}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Browse Offers
              </Button>
            </Stack>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        pb: 8,
      }}
      gap={2}
    >
      {/* Header */}
      <AppBar
        elevation={0}
        position="sticky"
        color="inherit"
        sx={{
          bgcolor: "#fff",
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 1 }}>
          <HistoryRoundedIcon color="inherit" sx={{ mr: 0.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>
            Purchase History
          </Typography>
          {Boolean(cusId) && (
            <Button
              aria-label="refresh"
              onClick={() => refetch()} // Wrap in arrow function
              disabled={isFetching}
              variant="contained"
              sx={{ boxShadow: "none" }}
            >
              {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {/* Filter bar */}

        {/* <PurchaseFilters
          status={status}
          setStatus={setStatus}
          dateRange={dateRange}
          setDateRange={setDateRange}
          clearFilters={clearFilters}
        /> */}

        {/* Loading */}
        {isLoading && (
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <PurchaseHistoryCardSkeleton />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Error */}
        {!isLoading && error && (
          <Card
            sx={{
              borderRadius: 3,
              p: 3,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <ErrorOutlineIcon color="error" />
              <Stack flex={1}>
                <Typography
                  variant="subtitle1"
                  fontWeight={900}
                  color="error.main"
                >
                  Error loading history
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {error.message}
                </Typography>
              </Stack>
              <IconButton
                color="primary"
                onClick={() => refetch()}
                sx={{ fontWeight: 700 }}
              >
                Try Again
              </IconButton>
            </Stack>
          </Card>
        )}

        {/* Empty */}
        {!isLoading && !error && entries.length === 0 && (
          <Card
            elevation={0}
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 4,
              background: "white",
            }}
          >
            <ShoppingBagOutlined sx={{ fontSize: 64, color: "primary.main" }} />
            <Typography variant="h5" fontWeight={900} mt={1}>
              No purchases found
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              Try changing the filters or check back later.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 3, borderRadius: 2, px: 3, fontWeight: 700 }}
              onClick={() => navigate("/")}
            >
              Start Shopping
            </Button>
          </Card>
        )}

        {/* Grouped by month */}
        {!isLoading && !error && entries.length > 0 && (
          <Stack spacing={3}>
            {entries.map(([ym, rows]) => {
              const label =
                ym === "unknown"
                  ? "Unknown Month"
                  : dayjs(ym + "-01").format("MMMM YYYY");
              return (
                <Box key={ym}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight={800}>
                      {label}
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Stack>
                  <Grid container spacing={2} alignItems="stretch">
                    {rows.map((order) => (
                      <Grid item xs={12} sm={6} md={4} key={order.orderId}>
                        <PurchaseHistoryCard order={order} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        )}
      </Container>
    </Stack>
  );
};

export default PurchasePage;
